import "./glossary-module.scss"

class Glossary {
    constructor(container) {
        if(!container) return;

        this.container = container

        this.build()
        this.bind()
    }

    build(){
        this.items = [...this.container.querySelectorAll('.glossary-item')]
        // Both ".glossary-letters li" (plain list) and ".glossary-categories li"
        // (by-categories) share the same click/active-state behaviour below -
        // only one of the two is ever present in a given module instance.
        this.nav = [...this.container.querySelectorAll('.glossary-letters li, .glossary-categories li')]
        // Section markers already used to scroll to a given letter/category -
        // reused as-is for the scroll-spy below, no extra markup needed.
        this.sections = [...this.container.querySelectorAll('.glossary-index, .glossary-category')]
        this.termsContainer = this.container.querySelector('.glossary-terms')
        this.header = document.querySelector('header')
        this.hHeight = this.getScrollOffset()

        this.closeItems()
        this.observeSections()

        if(window.location.hash) {
            let element = document.querySelector(`[data-term="${CSS.escape(window.location.hash.replace("#", ""))}"]`)
            this.scrollTo(element)
        }
    }

    bind(){
        this.items.forEach(item => {
            item.addEventListener('click', () => {
                this.openItem(item)
                history.pushState(null, '', `${window.location.pathname}#${item.dataset.term}`)
            })
        })

        this.nav.forEach(navItem => {
            navItem.addEventListener('click', () => {
                const anchor = navItem.dataset.letter ?? navItem.dataset.category
                const selector = 'letter' in navItem.dataset
                    ? `[data-letter="${CSS.escape(anchor)}"]`
                    : `[data-category="${CSS.escape(anchor)}"]`
                const target = this.termsContainer.querySelector(selector)

                // Reflect the click immediately instead of waiting for the
                // scroll-spy observer to catch up with the smooth scroll below.
                this.setActiveNav(anchor)
                this.closeItems()
                    .then(() => this.scrollTo(target))
                history.pushState(null, '', `${window.location.pathname}#${anchor}`)
            })
        })
    }

    /**
     * Scroll-spy: highlights the nav item (letter or category) matching
     * whichever section is currently crossing the top of the viewport, using
     * an IntersectionObserver on the same ".glossary-index"/".glossary-category"
     * markers the click handlers above already scroll to.
     */
    observeSections(){
        if (!this.sections.length || !('IntersectionObserver' in window)) return;

        this.sectionObserver = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter(entry => entry.isIntersecting)
                if (!visible.length) return;

                // Several sections can be reported in the same batch (short
                // sections, fast scroll) - keep the one furthest down the page,
                // i.e. closest to the activation line defined by rootMargin.
                const active = visible.reduce((a, b) =>
                    this.sections.indexOf(b.target) > this.sections.indexOf(a.target) ? b : a
                )

                this.setActiveNav(active.target.dataset.letter ?? active.target.dataset.category)
            },
            {
                // Activation line just below the (sticky) header, ignoring
                // everything past the top 30% of the remaining viewport.
                rootMargin: `-${Math.round(this.hHeight)}px 0px -70% 0px`,
                threshold: 0,
            }
        )

        this.sections.forEach(section => this.sectionObserver.observe(section))
    }

    /**
     * Vertical offset kept between the top of the viewport and whatever we
     * scroll to (a letter/category section, or a term via the URL hash) -
     * used both by scrollTo() and as the IntersectionObserver's activation
     * line in observeSections() - so the target doesn't end up hidden under
     * a fixed/sticky nav.
     *
     * Override it per project with the `--glossary-scroll-offset` CSS custom
     * property, set on `.module-glossary` / `.module-glossary-by-categories`
     * or higher up (it inherits, so :root works too if every instance needs
     * the same offset). Any valid CSS length works, including a calc()/var()
     * chain (e.g. `calc(var(--size-header) + var(--space-6))`) - it's resolved
     * the same way the browser would resolve it on a real layout property,
     * see resolveCssLength() below. It always wins when present. Without it,
     * falls back to auto-detecting the rendered height of <header> (+ 50px
     * breathing room) - which only gives the right value when the fixed/
     * sticky nav actually *is*, or is fully contained in, a <header> element;
     * set the custom property whenever that's not the case, or whenever the
     * auto-detected value is off.
     */
    getScrollOffset(){
        const resolved = this.resolveCssLength('var(--glossary-scroll-offset, -1px)')
        if (resolved !== null && resolved >= 0) return resolved

        return this.header ? this.header.getBoundingClientRect().height + 50 : 0
    }

    /**
     * Resolves a CSS length (possibly a calc()/var() chain) to a definite
     * pixel number, the same way the browser would for a real layout property.
     *
     * getComputedStyle(el).getPropertyValue('--some-custom-property') is NOT
     * enough for this: custom properties keep their *unresolved* value - a
     * calc()/var() expression comes back as that literal text, not a number,
     * so a plain parseFloat() on it returns NaN. Assigning the value to an
     * actual CSS property (here: height, on a throwaway probe element placed
     * inside the container so it inherits the same cascade) forces the
     * browser to do the resolution, and reading that property back with
     * getComputedStyle() then gives a definite "…px" string.
     */
    resolveCssLength(value){
        const probe = document.createElement('div')
        probe.style.cssText = `position: absolute; width: 0; visibility: hidden; pointer-events: none; height: ${value};`
        this.container.appendChild(probe)
        const pixels = parseFloat(getComputedStyle(probe).height)
        probe.remove()

        return Number.isNaN(pixels) ? null : pixels
    }

    setActiveNav(anchor){
        this.nav.forEach(navItem => {
            navItem.classList.toggle('active', (navItem.dataset.letter ?? navItem.dataset.category) === anchor)
        })
    }

    scrollTo(target){
        if(!target) return;
        const yPos = target.getBoundingClientRect().top - this.hHeight + window.scrollY
        this.openItem(target)
        window.scroll({top: yPos, left: 0, behavior: "smooth"})
    }

    openItem(item){
        let title = item.querySelector('.glossary-title')
        let content = item.querySelector('.glossary-content')
        if(!title) return;
        title = title.getBoundingClientRect()
        content = content.getBoundingClientRect()
        this.closeItems()
        item.style.height = title.height + content.height + 'px'
        item.classList.add('open')
    }

    closeItem(item){
        return new Promise(res => {
            let title = item.querySelector('.glossary-title').getBoundingClientRect()
            item.style.height = title.height + 'px'
            item.classList.remove('open')
            let timeout = window.getComputedStyle(item)['transition-duration']
            if(timeout.match('ms')) timeout = parseFloat(timeout)
            else timeout = parseFloat(timeout) * 1000;
            setTimeout(res, timeout)
        })
    }
    closeItems(){
        return Promise.allSettled(
            this.items.map(i => this.closeItem(i))
        )
    }
}

window.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll('.module-glossary, .module-glossary-by-categories')?.forEach(el => new Glossary(el))
})
